import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Ruler,
  Scale,
  Info,
  Ship,
  Shield,
  CheckCircle,
  Headphones,
  Wallet,
  CircleDollarSign,
  ArrowRight,
} from 'lucide-react';
import heroBg from '../assets/auth/logistics-hero.png';
import '../styles/pricing-page.css';
import API_BASE from '../services/apiBase';
import { computeCbmFromDimensions } from '../utils/cbm';

const formatRm = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `RM ${v.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const resolveUploadUrl = (path, cacheKey = '') => {
  if (!path) return '';
  const resolved = /^https?:\/\//i.test(path)
    ? path
    : path.startsWith('/')
      ? `${API_BASE}${path}`
      : path;
  if (!cacheKey) return resolved;
  const joiner = resolved.includes('?') ? '&' : '?';
  return `${resolved}${joiner}v=${encodeURIComponent(cacheKey)}`;
};

const inchToCm = (inch) => {
  const n = Number.parseFloat(inch);
  if (!Number.isFinite(n)) return null;
  return (n * 2.54).toFixed(1);
};

const INCLUDED_ITEMS = [
  'Sea consolidation Malaysia → Sri Lanka',
  'Published per-box rates (UPB / gift & door-to-door)',
  'CBM-based sizing for standard JET boxes',
  'Customs and documentation guidance',
  'Customer notifications on milestones',
  'Optional cargo insurance on request',
];

const TRUST_ITEMS = [
  {
    Icon: Wallet,
    title: 'No hidden fees',
    text: 'Clear box-level pricing with line items you can compare.',
  },
  {
    Icon: CircleDollarSign,
    title: 'Competitive sea rates',
    text: 'Rates tuned for the Malaysia–Sri Lanka corridor.',
  },
  {
    Icon: Shield,
    title: 'Secure & reliable',
    text: 'Audited partners and proactive shipment monitoring.',
  },
  {
    Icon: Headphones,
    title: '24/7 support',
    text: 'Operations desk coverage for time-sensitive moves.',
  },
];

const Pricing = () => {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE}/api/empty-boxes`);
        if (!res.ok) throw new Error('Could not load pricing');
        const data = await res.json();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setBoxes(list.filter((b) => (b.status || 'Active') === 'Active'));
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load box pricing');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="price-page">
      <section className="price-hero" aria-labelledby="price-hero-title">
        <div className="price-hero__bg" style={{ backgroundImage: `url(${heroBg})` }} />
        <div className="price-hero__overlay" />
        <div className="price-hero__inner">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="price-hero__crumb">
              <Link to="/">Home</Link>
              <span aria-hidden> &gt; </span>
              Pricing
            </p>
            <h1 id="price-hero-title" className="price-hero__title">
              Transparent Pricing. Tailored to Your Needs.
            </h1>
            <p className="price-hero__lead">
              Sea freight from Malaysia to Sri Lanka: standard JET box charges with UPB / gift and door-to-door options.
              Rates are published from our live catalog.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="price-toolbar">
        <div className="price-toolbar__inner price-toolbar__inner--lane">
          <p className="price-lane" role="status">
            <Ship className="w-5 h-5 price-lane__icon" aria-hidden strokeWidth={2} />
            <span className="price-lane__strong">Malaysia → Sri Lanka</span>
            <span className="price-lane__sep" aria-hidden>
              ·
            </span>
            <span>Sea freight box charges</span>
          </p>
        </div>
      </div>

      <div className="price-layout">
        <aside className="price-aside">
          <h2>What&apos;s included?</h2>
          <ul>
            {INCLUDED_ITEMS.map((item) => (
              <li key={item}>
                <CheckCircle className="w-4 h-4" strokeWidth={2.2} aria-hidden />
                {item}
              </li>
            ))}
          </ul>
          <div className="price-aside__cta">
            <h3>
              <Headphones className="w-4 h-4" aria-hidden />
              Need a custom quote?
            </h3>
            <p>Tell us your lane, commodity, and timeline—we&apos;ll tailor a proposal.</p>
            <Link to="/contact?intent=quote">
              Request a quote
              <ArrowRight className="w-4 h-4" aria-hidden />
            </Link>
          </div>
        </aside>

        <div className="price-box-main">
          <h2 className="price-box-main__title" id="price-box-charges-title">
            Box charges
          </h2>
          <p className="price-box-main__lead">
            All amounts below are <strong>Malaysian Ringgit (RM)</strong> per standard box for this lane, as set in your admin
            catalog.
          </p>

          {loading && <p style={{ textAlign: 'center', color: 'var(--p-muted)', padding: '2rem' }}>Loading pricing…</p>}
          {!loading && error && (
            <p style={{ textAlign: 'center', color: '#b91c1c', padding: '2rem' }}>{error}</p>
          )}
          {!loading && !error && boxes.length === 0 && (
            <p style={{ textAlign: 'center', color: 'var(--p-muted)', padding: '2rem' }}>
              No active boxes are published yet. Please check back soon or contact us for a quote.
            </p>
          )}

          {!loading && !error && boxes.length > 0 && (
            <div className="price-catalog__grid">
              {boxes.map((box, index) => {
                const L = box.lengthInch;
                const W = box.widthInch;
                const H = box.heightInch;
                const lCm = inchToCm(L);
                const wCm = inchToCm(W);
                const hCm = inchToCm(H);
                const dimensionsCm = lCm && wCm && hCm ? `${lCm} × ${wCm} × ${hCm} cm` : '—';
                const dimensionsInch =
                  L != null && W != null && H != null ? `${L}" × ${W}" × ${H}"` : '—';
                const cbmStr = computeCbmFromDimensions('inch', L, W, H);
                const cbmDisplay = cbmStr === '' ? '—' : `${cbmStr} m³`;
                const imgSrc = resolveUploadUrl(box.boxImageUrl, box.updatedAt || box._id || '');
                const weightLabel =
                  box.weightKg !== undefined && box.weightKg !== null && String(box.weightKg).trim() !== ''
                    ? `${box.weightKg} kg`
                    : '—';

                return (
                  <motion.div
                    key={box._id || box.boxName}
                    className="card"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.4 }}
                    whileHover={{ scale: 1.01 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                      <div className="price-box-thumb">
                        {imgSrc ? (
                          <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ fontSize: '1.75rem' }} aria-hidden>
                            📦
                          </span>
                        )}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <h3 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 0.25rem', color: 'var(--p-navy)' }}>
                          {box.boxName || 'Box'}
                        </h3>
                        {box.secondName ? (
                          <p style={{ color: 'var(--p-muted)', fontSize: '0.9rem', margin: '0 0 0.5rem' }}>
                            {box.secondName}
                          </p>
                        ) : null}
                        {box.description ? (
                          <p style={{ color: 'var(--p-muted)', fontSize: '0.875rem', margin: 0 }}>{box.description}</p>
                        ) : null}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Ruler className="w-4 h-4" style={{ color: 'var(--p-blue)' }} />
                        <div>
                          <p style={{ fontSize: '0.75rem', color: 'var(--p-muted)' }}>Dimensions</p>
                          <p className="price-dim-value">{dimensionsCm}</p>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{dimensionsInch}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Scale className="w-4 h-4" style={{ color: 'var(--p-blue)' }} />
                        <div>
                          <p style={{ fontSize: '0.75rem', color: 'var(--p-muted)' }}>Weight</p>
                          <p className="price-dim-value">{weightLabel}</p>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                      <div className="price-stat-tile price-stat-tile--cbm">
                        <p className="price-stat-tile__label">CBM</p>
                        <p className="price-stat-tile__value">{cbmDisplay}</p>
                      </div>
                      <div className="price-stat-tile price-stat-tile--upb">
                        <p className="price-stat-tile__label">UPB / Gift</p>
                        <p className="price-stat-tile__value price-stat-tile__value--upb">{formatRm(box.priceUpbGift)}</p>
                      </div>
                      <div className="price-stat-tile price-stat-tile--d2d">
                        <p className="price-stat-tile__label">Door to door</p>
                        <p className="price-stat-tile__value price-stat-tile__value--d2d">
                          {formatRm(box.priceDoorToDoor)}
                        </p>
                      </div>
                    </div>

                    {typeof box.quantity === 'number' && (
                      <p style={{ fontSize: '0.78rem', color: 'var(--p-muted)', margin: 0, textAlign: 'center' }}>
                        Stock availability: {box.quantity}
                      </p>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}

          <motion.div
            className="card price-notes-card"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <Info className="w-6 h-6" style={{ flexShrink: 0, marginTop: '0.2rem', color: 'var(--p-blue)' }} />
              <div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--p-navy)' }}>
                  Important notes
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '0.45rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <span style={{ color: 'var(--p-blue)', fontWeight: 700 }}>•</span>
                    <span>Listed box prices come from your live catalog and may change when admins update rates.</span>
                  </li>
                  <li style={{ marginBottom: '0.45rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <span style={{ color: 'var(--p-blue)', fontWeight: 700 }}>•</span>
                    <span>Additional charges may apply for handling, insurance, or customs.</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <span style={{ color: 'var(--p-blue)', fontWeight: 700 }}>•</span>
                    <span>Contact us for oversized cargo or other lanes not covered by this Malaysia–Sri Lanka sea box list.</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <section className="price-trust" aria-label="Why book with us">
        <div className="price-trust__inner">
          {TRUST_ITEMS.map(({ Icon, title, text }) => (
            <div key={title} className="price-trust__item">
              <Icon className="w-5 h-5" strokeWidth={1.85} aria-hidden />
              <div>
                <strong>{title}</strong>
                <span>{text}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Pricing;
